import React, { useRef, useState } from 'react'
import { Box, Typography, IconButton, CircularProgress, Button, LinearProgress, FormHelperText } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { fileUpload, fileDelete } from '../../api/uploadApi'
import ValidationErrorText from '../common/ValidationErrorText'

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp']

const UniversityLogo = ({
  label = "University Logo",
  value = "",
  onChange,
  error = '',
  setError = () => { },
  readOnly = false,
  fieldError,
}) => {
  const fileInputRef = useRef(null)
  // Progress and status managed locally
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const handleFileChange = async (e) => {
    const uploaded = e.target.files[0]
    if (!uploaded) return
    if (!ALLOWED_TYPES.includes(uploaded.type)) {
      setError('Unsupported image type.')
      return
    }
    setError('')
    setUploading(true)
    setProgress(0)
    try {
      const data = await fileUpload(uploaded, prog => setProgress(prog))
      if (data.url) {
        onChange(data.url)
      } else {
        setError('Logo upload failed (no url)!')
      }
    } catch (e) {
      setError('Logo upload failed!')
    } finally {
      setUploading(false)
      setProgress(0)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveLogo = async () => {
    setError('')
    if (value) {
      try {
        await fileDelete(value)
      } catch (err) {
        setError('Failed to delete logo from server')
        return
      }
    }
    onChange("")
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <>
    <Box display="flex" alignItems="center" gap={2}>
      <input
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={readOnly || uploading}
      />

      <Box
        width="120px"
        height="120px"
        border="1px dashed #0463EF"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius={2}
        position="relative"
        sx={{
          cursor: readOnly ? 'not-allowed' : 'pointer',
          background: 'rgba(4, 99, 239, 0.06)',
          minWidth: '120px'
        }}
        onClick={() => {
          if (!readOnly && !uploading && fileInputRef.current) fileInputRef.current.click()
        }}
      >
        {value && !uploading ? (
          <>
            <Box
              component="img"
              src={value}
              alt="University Logo"
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
            {!readOnly && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  handleRemoveLogo()
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  padding: '2px',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : uploading ? (
          <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <CircularProgress size={28} thickness={5} />
            <Typography fontSize="11px" mt={1}>{progress}%</Typography>
            <Box sx={{ width: '80%', mt: 1 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 5 }} />
            </Box>
          </Box>
        ) : (
          <Typography
            fontSize="13px"
            fontWeight="500"
            color="#363853"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1,
              textAlign: 'center',
              pointerEvents: readOnly ? 'none' : 'auto',
              opacity: readOnly ? 0.5 : 1,
            }}
          >
            <AddIcon sx={{ fill: '#1075FF' }} />
            Click to upload
          </Typography>
        )}
      </Box>

      <Box>
        <Typography fontSize="14px" fontWeight="500" mb={0.5}>
          {label}
        </Typography>
        <Typography fontSize="12px" color="textSecondary" sx={{ maxWidth: '354px', mb: 1 }}>
          Please upload a high-resolution PNG, JPG, JPEG, SVG, or WEBP image with a transparent background,
          1:1 aspect ratio, minimum 300×300 pixels, max 1MB.
        </Typography>
        {error && (
          <Typography color="error" fontSize={13} mb={1}>{error}</Typography>
        )}

        {/* {!readOnly && (
          <Button
            variant="outlined"
            component="label"
            startIcon={<AddIcon />}
            sx={{ color: '#1075FF', border: '1px solid #1075FF', textTransform: 'capitalize', px: 3 }}
            disabled={uploading}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {value ? 'Replace Logo' : 'Upload Logo'}
          </Button>
        )} */}
      </Box>
    </Box>
    <ValidationErrorText error={fieldError}/>
    </>
  )
}

export default UniversityLogo
